import { v } from "convex/values";

// ============= TIPOS Y VALIDADORES DE REPORTE =============

export const reportStatus = v.union(
  v.literal("active"),
  v.literal("paused"),
  v.literal("archived"),
  v.literal("completed"),
);

export const reportVisibility = v.union(
  v.literal("public"), // Visible para todos en la empresa
  v.literal("private"), // Solo el creador
  v.literal("assigned"), // Solo usuarios asignados
);

export const reportFormat = v.union(
  v.literal("pdf"),
  v.literal("csv"),
  v.literal("json"),
  v.literal("excel"),
);

export const reportFrequency = v.union(
  v.literal("daily"),
  v.literal("weekly"),
  v.literal("monthly"),
  v.literal("custom"),
  v.literal("once"),
);

// Tipos de rango de fecha dinamico para reportes programados
export const dateRangeType = v.union(
  v.literal("fixed"), // Rango fijo (from/to especificos)
  v.literal("today"), // Hoy
  v.literal("yesterday"), // Ayer
  v.literal("last_7_days"), // Ultimos 7 dias
  v.literal("last_30_days"), // Ultimos 30 dias
  v.literal("this_week"), // Esta semana (lunes a hoy)
  v.literal("last_week"), // Semana pasada
  v.literal("this_month"), // Este mes
  v.literal("last_month"), // Mes pasado
  v.literal("this_quarter"), // Este trimestre
  v.literal("last_quarter"), // Trimestre pasado
  v.literal("this_year"), // Este año
  v.literal("last_year"), // Año pasado
  v.literal("custom_days"), // Ultimos N dias personalizados
);

// ============= OPERADORES Y AGREGACIONES =============

export const filterOperator = v.union(
  v.literal("equals"),
  v.literal("notEquals"),
  v.literal("contains"),
  v.literal("startsWith"),
  v.literal("endsWith"),
  v.literal("greaterThan"),
  v.literal("lessThan"),
  v.literal("greaterThanOrEqual"),
  v.literal("lessThanOrEqual"),
  v.literal("between"),
  v.literal("in"),
  v.literal("notIn"),
);

export const aggregationOperation = v.union(
  v.literal("sum"),
  v.literal("avg"),
  v.literal("min"),
  v.literal("max"),
  v.literal("count"),
  v.literal("countDistinct"),
);

export const sortDirection = v.union(v.literal("asc"), v.literal("desc"));

// ============= VALIDADORES DE OBJETOS COMPLEJOS =============

export const filterValidator = v.object({
  field: v.string(),
  operator: filterOperator,
  value: v.any(),
  dateRange: v.optional(
    v.object({
      from: v.number(),
      to: v.number(),
    }),
  ),
  // Rango dinamico para reportes programados
  dynamicDateRange: v.optional(
    v.object({
      type: dateRangeType,
      customDays: v.optional(v.number()), // Para type="custom_days"
    }),
  ),
});

// Funcion helper para calcular el rango de fechas dinamico
export function calculateDynamicDateRange(
  type: string,
  customDays?: number,
): { from: number; to: number } {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const todayEnd = new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1);

  switch (type) {
    case "today":
      return { from: today.getTime(), to: todayEnd.getTime() };

    case "yesterday": {
      const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
      const yesterdayEnd = new Date(today.getTime() - 1);
      return { from: yesterday.getTime(), to: yesterdayEnd.getTime() };
    }

    case "last_7_days": {
      const from = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      return { from: from.getTime(), to: todayEnd.getTime() };
    }

    case "last_30_days": {
      const from = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
      return { from: from.getTime(), to: todayEnd.getTime() };
    }

    case "this_week": {
      // Lunes de esta semana
      const dayOfWeek = today.getDay();
      const monday = new Date(
        today.getTime() -
          (dayOfWeek === 0 ? 6 : dayOfWeek - 1) * 24 * 60 * 60 * 1000,
      );
      return { from: monday.getTime(), to: todayEnd.getTime() };
    }

    case "last_week": {
      const dayOfWeek = today.getDay();
      const thisMonday = new Date(
        today.getTime() -
          (dayOfWeek === 0 ? 6 : dayOfWeek - 1) * 24 * 60 * 60 * 1000,
      );
      const lastMonday = new Date(
        thisMonday.getTime() - 7 * 24 * 60 * 60 * 1000,
      );
      const lastSunday = new Date(thisMonday.getTime() - 1);
      return { from: lastMonday.getTime(), to: lastSunday.getTime() };
    }

    case "this_month": {
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: firstDay.getTime(), to: todayEnd.getTime() };
    }

    case "last_month": {
      const firstDayLastMonth = new Date(
        now.getFullYear(),
        now.getMonth() - 1,
        1,
      );
      const lastDayLastMonth = new Date(
        now.getFullYear(),
        now.getMonth(),
        0,
        23,
        59,
        59,
        999,
      );
      return {
        from: firstDayLastMonth.getTime(),
        to: lastDayLastMonth.getTime(),
      };
    }

    case "this_quarter": {
      const quarter = Math.floor(now.getMonth() / 3);
      const firstDayQuarter = new Date(now.getFullYear(), quarter * 3, 1);
      return { from: firstDayQuarter.getTime(), to: todayEnd.getTime() };
    }

    case "last_quarter": {
      const quarter = Math.floor(now.getMonth() / 3);
      const lastQuarter = quarter === 0 ? 3 : quarter - 1;
      const year = quarter === 0 ? now.getFullYear() - 1 : now.getFullYear();
      const firstDayLastQuarter = new Date(year, lastQuarter * 3, 1);
      const lastDayLastQuarter = new Date(
        year,
        lastQuarter * 3 + 3,
        0,
        23,
        59,
        59,
        999,
      );
      return {
        from: firstDayLastQuarter.getTime(),
        to: lastDayLastQuarter.getTime(),
      };
    }

    case "this_year": {
      const firstDayYear = new Date(now.getFullYear(), 0, 1);
      return { from: firstDayYear.getTime(), to: todayEnd.getTime() };
    }

    case "last_year": {
      const firstDayLastYear = new Date(now.getFullYear() - 1, 0, 1);
      const lastDayLastYear = new Date(
        now.getFullYear() - 1,
        11,
        31,
        23,
        59,
        59,
        999,
      );
      return {
        from: firstDayLastYear.getTime(),
        to: lastDayLastYear.getTime(),
      };
    }

    case "custom_days": {
      const days = customDays || 7;
      const from = new Date(today.getTime() - days * 24 * 60 * 60 * 1000);
      return { from: from.getTime(), to: todayEnd.getTime() };
    }

    case "fixed":
    default:
      // Para fixed, se debe usar dateRange directamente
      return { from: today.getTime(), to: todayEnd.getTime() };
  }
}

// Etiquetas en espanol para los tipos de rango dinamico
export const DATE_RANGE_TYPE_LABELS: Record<string, string> = {
  fixed: "Rango fijo",
  today: "Hoy",
  yesterday: "Ayer",
  last_7_days: "Ultimos 7 dias",
  last_30_days: "Ultimos 30 dias",
  this_week: "Esta semana",
  last_week: "Semana pasada",
  this_month: "Este mes",
  last_month: "Mes pasado",
  this_quarter: "Este trimestre",
  last_quarter: "Trimestre pasado",
  this_year: "Este año",
  last_year: "Año pasado",
  custom_days: "Ultimos N dias",
};

export const aggregationValidator = v.object({
  field: v.string(),
  operation: aggregationOperation,
});

export const sortValidator = v.object({
  field: v.string(),
  direction: sortDirection,
});

export const reportConfigValidator = v.object({
  filters: v.array(filterValidator),
  aggregations: v.optional(v.array(aggregationValidator)),
  sorts: v.optional(v.array(sortValidator)),
  groupBy: v.optional(v.array(v.string())),
  columns: v.array(v.string()),
});

export const scheduleValidator = v.object({
  frequency: v.optional(reportFrequency),
  time: v.optional(v.string()), // Formato "HH:mm"
  dayOfWeek: v.optional(v.number()), // 0-6 (domingo-sábado)
  dayOfMonth: v.optional(v.number()), // 1-31
  customCron: v.optional(v.string()), // Expresión cron custom
  timezone: v.optional(v.string()), // "America/Tegucigalpa"
});

export const distributionValidator = v.object({
  email: v.object({
    enabled: v.boolean(),
    recipients: v.optional(v.array(v.string())),
  }),
  notification: v.optional(v.boolean()),
  webhook: v.optional(
    v.object({
      enabled: v.boolean(),
      url: v.optional(v.string()),
    }),
  ),
  storage: v.optional(
    v.object({
      enabled: v.boolean(),
      path: v.optional(v.string()),
    }),
  ),
});

// ============= TIPOS DE EJECUCIÓN =============

export const reportExecutionStatus = v.union(
  v.literal("running"),
  v.literal("completed"),
  v.literal("failed"),
);

// ============= TIPOS DE NOTIFICACIÓN =============

export const notificationType = v.union(
  v.literal("report_ready"),
  v.literal("report_failed"),
  v.literal("report_shared"),
  v.literal("message_received"),
);
